export interface ReactRouterLocation {
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
  key: string;
}

export interface AddRedirectParamOptions {
  targetPath: URL;
  redirectTo: Location | URL;
}

export interface GetViewerUrlParams {
  fileVersionId?: string;
  transmittalVersionId?: string;
  issueId?: string;
  estimateId?: string;
  measurementId?: string;
  versionId?: string;
  spaceId?: string;
  scheduleId?: string;
  // Optional: getViewerUrl returns early (undefined) when projectId is falsy (see guard below).
  projectId?: string;
  contractId?: string;
  preloadAndOpen?: boolean;
  mode?: "scs" | "skipProperties";
  shortcutParentId?: string;
  clashDetectionId?: string;
  scheduleVersionId?: string;
  redirectTo?: URL;
  frontEndOrigin?: string;
  searchCondition?: string;
  searchValue?: string;
}

export const addRedirectParam = ({
  targetPath,
  redirectTo
}: AddRedirectParamOptions): string => {
  const targetPathStr = targetPath.pathname;
  const targetSearchParams = new URLSearchParams(targetPath.search);
  const targetHash = targetPath.hash;
  const currentPath = `${redirectTo.pathname}${redirectTo.search}${redirectTo.hash}`;
  targetSearchParams.set('redirectUrl', encodeURIComponent(currentPath));
  return `${targetPathStr}?${targetSearchParams.toString()}${targetHash}`;
};

export const extractRedirectUrl = (urlOrLocation: URL | ReactRouterLocation): URL => {
  const fallbackRoute = '/dashboard';
  let searchParams: URLSearchParams;

  if ('searchParams' in urlOrLocation) {
    searchParams = urlOrLocation.searchParams;
  } else {
    searchParams = new URLSearchParams(urlOrLocation.search);
  }

  const redirectUrl = searchParams.get('redirectUrl');

  let origin;
  const isClient = typeof globalThis !== 'undefined' && globalThis.location;
  if (isClient) {
    origin = globalThis.location.origin;
  } else {
    origin = process.env.FRONTEND_SOCIAL_LOGIN_URL;
  }
  
  if (redirectUrl) {
    const decodedUrl = decodeURIComponent(redirectUrl);
    return new URL(decodedUrl, origin);
  }
  return new URL(fallbackRoute, origin);
};

export const extractRedirectUrlAsString = (urlOrLocation: URL | ReactRouterLocation): string => {
  const fallbackRoute = '/dashboard';
  let searchParams: URLSearchParams;
  
  if ('searchParams' in urlOrLocation) {
    searchParams = urlOrLocation.searchParams;
  } else {
    searchParams = new URLSearchParams(urlOrLocation.search);
  }
  
  const redirectUrl = searchParams.get('redirectUrl');
  if (redirectUrl) {
    const decodedUrl = decodeURIComponent(redirectUrl);
    return decodedUrl;
  }
  return fallbackRoute;
};

export const getViewerUrl = ({
  fileVersionId, 
  transmittalVersionId, 
  issueId, 
  estimateId, 
  measurementId, 
  versionId, 
  spaceId, 
  scheduleId, 
  projectId, 
  contractId,
  preloadAndOpen,
  mode,
  shortcutParentId,
  clashDetectionId, 
  scheduleVersionId,
  redirectTo,
  frontEndOrigin,
  searchCondition,
  searchValue,
}: GetViewerUrlParams): string | undefined => {
  if (!projectId) {
    return;
  }
  const location = `/designviewer/file/${fileVersionId}`;
  const paramMappings: Record<string, string | undefined> = {
    scheduleId,
    projectId,
    spaceId,
    contractId,
    transmittalId: transmittalVersionId,
    shortcutParentId,
    issueId,
    estimateId,
    measurementId,
    versionId,
    clashDetectionId,
    scheduleVersionId,
    mode: mode ?? (preloadAndOpen ? 'scs' : undefined),
    searchCondition,
    searchValue,
  };
  const searchParams = new URLSearchParams();
  for(const [key, value] of Object.entries(paramMappings)) {
    if (value) {
      searchParams.set(key, value);
    }
  }
  const baseUrl = `${location}?${searchParams.toString()}`;
  const isClient = typeof globalThis !== 'undefined' && globalThis.location;
  const finalRedirectTo = redirectTo || (isClient ? globalThis.location : null);
  
  if (!finalRedirectTo) {
    return baseUrl;
  }
  
  const origin = isClient 
    ? globalThis.location.origin 
    : frontEndOrigin;

  return addRedirectParam({
    redirectTo: finalRedirectTo,
    targetPath: new URL(baseUrl, origin),
  });
};

export const getInspectionUrl = ({
  projectId, 
  resourceId, 
  selectedTab, 
  resourceType, 
  contractId
}: any) => {
  const url = `/project/${projectId}/forms/${resourceId}`;
  const searchParams = new URLSearchParams('');

  searchParams.set('projectId', projectId);
  if (contractId) {
    searchParams.set('contractId', contractId);
  } 
  if (resourceId) {
    searchParams.set('resourceId', resourceId);
  }
  if (resourceType) {
    searchParams.set('resourceType', resourceType);
  }
  return `${url}?${searchParams.toString()}`;
};